import React, { useRef, useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/client";
import appInfo from "utility/app-info";
import { LEARNER_ARTICLE } from "utility/graphQl/query";
import { logAnalyticsEvent } from "utility/FirebaseAnalytics";
import {
  LearnerArticle,
  LearnerArticleFeedbackMutation,
  LearnerArticleFeedbackMutationVariables,
} from "apps/user/learner/api/types";
import { LEARNER_ARTICLE_FEEDBACK } from "utility/graphQl/mutation";
import { useAuth } from "providers/auth";
import { usePage } from "providers/page";

import useReportArticleEngagement from "./hooks/use-report-article-engagement";
import useFavorite from "apps/user/learner/hooks/use-favourite";
import { IconButton } from "components/ui";
import { Button } from "components/ui";
import { ReactComponent as HeartIcon } from "Assets/icon/heart-icon.svg";
import {
  PageContainer,
  PageHeader,
  PageContent,
  PageLoading,
  PageError,
} from "apps/user/learner/components/page-container";
import styles from "./index.module.scss";
import { logError } from "utility/Function";
import { trackEvent } from "utility/mixpanel";
import { useTranslation } from "lib/i18n/use-translation";

type FeedbackOption = {
  id: string;
  label: string;
  value: number;
};

// `feedbackOptions` and `feedbackQuestion` are submitted to the API and to
// Mixpanel, so they stay canonical English; the catalog keys below supply the
// copy the learner actually sees.
const feedbackOptions: FeedbackOption[] = [
  { id: "1", label: "Yes", value: 1 },
  { id: "2", label: "I'm not sure", value: 2 },
  { id: "3", label: "No", value: 3 },
];

const feedbackOptionLabelKeys: Record<string, string> = {
  "1": "feedback:steps.practice.options.yes",
  "2": "feedback:steps.practice.options.unsure",
  "3": "feedback:steps.practice.options.no",
};

const feedbackQuestion =
  "Did this article give you skills to help you manage stress in your daily life?";

const ArticleDetail = () => {
  const { t } = useTranslation(["feedback", "common"]);
  const location = useLocation();
  const { onPrefixPageTitle } = usePage();
  const { articleId } = useParams();
  const { refetchUserProfiles } = useAuth();

  const isViewSentRef = useRef(false);
  const [submitted, setSubmitted] = useState(false);

  const { onOpen } = useReportArticleEngagement(articleId);
  const { onFavourite, loading: isFavouriteLoading } = useFavorite();

  const [submitLearnerArticleFeedback] = useMutation<
    LearnerArticleFeedbackMutation,
    LearnerArticleFeedbackMutationVariables
  >(LEARNER_ARTICLE_FEEDBACK);

  const { loading, error, data } = useQuery(LEARNER_ARTICLE, {
    onCompleted() {
      onOpen();
    },
    onError(error) {
      logError("Error while fetching article detail", error);
    },
    variables: {
      id: articleId,
      appInfo,
    },
  });

  useEffect(() => {
    if (!isViewSentRef.current && data?.learnerArticle) {
      let listName, listPosition;

      if (location.state?.listName) {
        listName = location.state.listName;
      }

      if (location.state?.listPosition) {
        listPosition = location.state.listPosition;
      }

      const userId = localStorage.getItem("userID");
      const companyName = localStorage.getItem("companyName") || "NA";

      const { id, title } = data.learnerArticle as LearnerArticle;

      trackEvent("Article Viewed", {
        "Article ID": id,
        "Article Title": title,
        "List Name": listName,
        "List Position": listPosition,
      });

      logAnalyticsEvent("article_view", {
        user_id: userId,
        user_id_wone: userId,
        user_company_name: companyName,
        article_id: id,
        article_title: title,
        list_name: listName,
        list_position: listPosition,
        page_title: document.title,
        page_path: window.location.pathname,
      });

      isViewSentRef.current = true;
    }
  }, [data, location]);

  onPrefixPageTitle(data?.learnerArticle?.title);

  if (loading) {
    return (
      <PageContainer background="white">
        <PageHeader showBackButton />
        <PageContent maxWidth="sm">
          <PageLoading />
        </PageContent>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer background="white">
        <PageHeader showBackButton />
        <PageContent maxWidth="sm">
          <PageError />
        </PageContent>
      </PageContainer>
    );
  }

  const { id, title, subtitle, imageUrl, contentHtml, isFavourite } =
    data.learnerArticle;

  const handleFeedback = (response: FeedbackOption) => {
    refetchUserProfiles();
    const variables = {
      appInfo,
      input: {
        articleId: id,
        response: {
          id: "LearnerArticleFeedback",
          question: feedbackQuestion,
          options: feedbackOptions,
          response,
        },
      },
    };
    submitLearnerArticleFeedback({ variables })
      .then(() => {
        trackEvent("Article Feedback", {
          "Article ID": id,
          "Article Title": title,
          "Feedback Question": feedbackQuestion,
          "Feedback Response": response.id,
        });
        setSubmitted(true);
      })
      .catch((error) => {
        logError("Error submitting article feedback", error);
      });
  };

  return (
    <PageContainer background="white">
      <PageHeader showBackButton>
        <IconButton
          icon={
            <HeartIcon
              color={isFavourite ? "black" : "transparent"}
              stroke="black"
            />
          }
          loading={isFavouriteLoading}
          onClick={() => onFavourite(data.learnerArticle)}
          aria-label={
            isFavourite
              ? t("common:a11y.removeFromFavourites")
              : t("common:a11y.addToFavourites")
          }
          className={styles["favourite-button"]}
        />
      </PageHeader>
      <PageContent maxWidth="sm">
        <div className={styles["detail-page-main"]}>
          <div className={styles["detail-page-content"]}>
            <h1 className={styles["detail-page-main-heading"]}>{title}</h1>
            <p className={styles["detail-page-author"]}> {subtitle}</p>

            <img
              className={styles["detail-page-image"]}
              src={imageUrl}
              alt="detail-page-icon"
            />
            <article
              dangerouslySetInnerHTML={{ __html: contentHtml }}
              className={styles["detail-page-details"]}
            ></article>

            <div className={styles["article-feedback-container"]}>
              <div className={styles["feedback-message"]}>
                {submitted
                  ? t("feedback:toast.title")
                  : t("feedback:article.practiceQuestion")}
              </div>
              {!submitted && (
                <div className={styles["feedback-actions"]}>
                  {feedbackOptions.map((option) => (
                    <Button
                      key={option.id}
                      variant="secondary"
                      onClick={() => handleFeedback(option)}
                    >
                      {t(feedbackOptionLabelKeys[option.id])}
                    </Button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </PageContent>
    </PageContainer>
  );
};

export default ArticleDetail;
